import type { SessionStorage } from "@heraion/shared";

// ⚠️ Touching window.localStorage THROWS (SecurityError) in Safari with
// cookies blocked and in a sandboxed iframe, and at module load that is a
// blank app. Probed once, with the Cognito SDK's way out: memory, so what is
// stored lasts as long as the tab. Only a refused access goes there: a full
// store (QuotaExceededError) still reads, and the session on it is there.
// The session (auth/cognito.ts) and the app's caches read through it; the
// i18n detector keeps its own access.
const memoryStorage = (): SessionStorage => {
  const items = new Map<string, string>();
  return {
    getItem: (key) => items.get(key) ?? null,
    setItem: (key, value) => {
      items.set(key, value);
    },
    removeItem: (key) => {
      items.delete(key);
    },
    clear: () => {
      items.clear();
    },
  };
};

const refused = (err: unknown) =>
  (err as { name?: unknown } | null)?.name === "SecurityError";

const pickStorage = (): SessionStorage => {
  try {
    const local = typeof window === "undefined" ? undefined : window.localStorage;
    if (!local) return memoryStorage();
    try {
      local.setItem("aws.cognito.test-ls", "1");
      local.removeItem("aws.cognito.test-ls");
    } catch (err) {
      if (refused(err)) return memoryStorage();
    }
    return local;
  } catch {
    // Reading the property itself threw: the SecurityError case.
    return memoryStorage();
  }
};

export const webStorage: SessionStorage = pickStorage();
